/**
 * Core Permission System Types
 * 
 * This file defines all the core types for the unified permission system.
 * These types are based on the existing CLAUDE.md specifications and current codebase.
 */

// ============================================================================
// USER ROLES & HIERARCHY
// ============================================================================

/**
 * System-level user roles with clear hierarchy
 * ADMIN > COMPANY_ADMIN > USER
 */
export const _UserRole = {
  ADMIN: 'ADMIN',
  COMPANY_ADMIN: 'COMPANY_ADMIN',
  USER: 'USER'
} as const;

export type UserRole = typeof _UserRole[keyof typeof _UserRole];
// eslint-disable-next-line no-redeclare
export const UserRole = _UserRole;

/**
 * Project-level permission roles with clear hierarchy
 * OWNER > EDITOR > OPERATOR > VIEWER
 */
export const _ProjectPermission = {
  OWNER: 'OWNER',
  EDITOR: 'EDITOR',
  OPERATOR: 'OPERATOR',
  VIEWER: 'VIEWER'
} as const;

export type ProjectPermission = typeof _ProjectPermission[keyof typeof _ProjectPermission];
// eslint-disable-next-line no-redeclare
export const ProjectPermission = _ProjectPermission;

// ============================================================================
// PERMISSION ACTIONS & RESOURCES
// ============================================================================

/**
 * Standard CRUD + specialized actions
 */
export const _PermissionAction = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  ASSIGN: 'assign',
  APPROVE: 'approve',
  EXPORT: 'export',
  IMPORT: 'import',
  GENERATE: 'generate'
} as const;

export type PermissionAction = typeof _PermissionAction[keyof typeof _PermissionAction];
// eslint-disable-next-line no-redeclare
export const PermissionAction = _PermissionAction;

/**
 * System resources that can have permissions applied
 */
export const _PermissionResource = {
  COMPANY: 'company',
  PROJECT: 'project',
  SUBPROJECT: 'subproject',
  USER: 'user',
  TRUCK: 'truck',
  REPORT: 'report',
  ITEM: 'item',
  COMMENT: 'comment',
  ATTACHMENT: 'attachment',
  CONTRACT: 'contract',
  NEWSLETTER: 'newsletter'
} as const;

export type PermissionResource = typeof _PermissionResource[keyof typeof _PermissionResource];
// eslint-disable-next-line no-redeclare
export const PermissionResource = _PermissionResource;

// ============================================================================
// USER CONTEXT & CLAIMS
// ============================================================================

/**
 * Extended user context with all permission-relevant information
 */
export interface UserContext {
  id: string;
  email?: string;
  company_id?: string;
  company_type?: 'CLIENT' | 'FACILITATOR' | 'WORKSHOP' | null; // Denormalized for fast permission checks
  is_system_admin?: boolean;
  app_role?: UserRole;
  profile?: {
    role: UserRole;
    company_id: string | null;
    company?: {
      id: string;
      name: string;
      type: string;
    } | null;
  };
}

/**
 * Project membership information for permission calculations
 */
export interface ProjectMembership {
  assignment_id: string;
  user_id: string;
  permission_level: ProjectPermission;
  created_at: string;
}

/**
 * Company contract information for cross-company permissions
 */
export interface CompanyContract {
  id: string;
  company_one_id: string;
  company_two_id: string;
  is_deleted?: boolean;
}

// ============================================================================
// PERMISSION CHECK STRUCTURES
// ============================================================================

/**
 * Permission requirement specification
 */
export interface PermissionRequirement {
  action: PermissionAction;
  resource: PermissionResource;
  resourceId?: string | undefined;
  companyScope?: string | undefined;
  minimumRole?: UserRole | undefined;
  minimumProjectPermission?: ProjectPermission | undefined;
  context?: Record<string, unknown> | undefined;
}

/**
 * Permission check result with detailed information
 */
export interface PermissionResult {
  allowed: boolean;
  reason?: string;
  requiredRole?: UserRole;
  requiredProjectPermission?: ProjectPermission;
  userRole?: UserRole;
  userProjectPermission?: ProjectPermission;
  debugInfo?: {
    checkType: string;
    ruleName: string;
    evaluationPath: string[];
  };
}

/**
 * Permission cache entry
 */
export interface PermissionCacheEntry {
  key: string;
  result: PermissionResult;
  timestamp: number;
  ttl: number;
}

// ============================================================================
// SERVICE INTERFACES
// ============================================================================

/**
 * Core permission service interface
 */
export interface IPermissionService {
  // Core permission checking
  hasPermission(
    _user: UserContext,
    _requirement: PermissionRequirement
  ): Promise<PermissionResult>;

  // Convenience methods for common checks
  canViewCompany(_user: UserContext, _companyId: string): Promise<boolean>;
  canEditCompany(_user: UserContext, _companyId: string): Promise<boolean>;
  canDeleteCompany(_user: UserContext, _companyId: string): Promise<boolean>;
  
  canViewProject(_user: UserContext, _projectId: string): Promise<boolean>;
  canEditProject(_user: UserContext, _projectId: string): Promise<boolean>;
  canDeleteProject(_user: UserContext, _projectId: string): Promise<boolean>;
  
  canCreateUser(_user: UserContext, _targetCompanyId?: string): Promise<boolean>;
  canEditUser(_user: UserContext, _targetUserId: string): Promise<boolean>;
  canDeleteUser(_user: UserContext, _targetUserId: string): Promise<boolean>;

  // Project membership management
  canGrantProjectPermission(
    _user: UserContext,
    _projectId: string,
    _targetUserId: string,
    _permissionLevel: ProjectPermission
  ): Promise<boolean>;
  
  canRevokeProjectPermission(
    _user: UserContext,
    _projectId: string,
    _targetUserId: string
  ): Promise<boolean>;

  // Utility methods
  getUserRole(_user: UserContext): UserRole;
  getUserProjectPermission(_user: UserContext, _projectId: string): Promise<ProjectPermission | null>;
  isSystemAdmin(_user: UserContext): boolean;
  isCompanyAdmin(_user: UserContext): boolean;
}

/**
 * Permission cache service interface
 */
export interface IPermissionCache {
  get(_key: string): PermissionCacheEntry | null;
  set(_key: string, _result: PermissionResult, _ttl?: number): void;
  clear(_pattern?: string): void;
  clearUser(_userId: string): void;
}

// ============================================================================
// VALIDATION & ERROR TYPES
// ============================================================================

/**
 * Permission validation errors
 */
export class PermissionError extends Error {
  constructor(
    message: string,
    public _code: string,
    public _requirement?: PermissionRequirement,
    public _userContext?: UserContext
  ) {
    super(message);
    this.name = 'PermissionError';
  }
}

export const PermissionErrorCodes = {
  INSUFFICIENT_ROLE: 'INSUFFICIENT_ROLE',
  INSUFFICIENT_PROJECT_PERMISSION: 'INSUFFICIENT_PROJECT_PERMISSION',
  COMPANY_SCOPE_VIOLATION: 'COMPANY_SCOPE_VIOLATION',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  INVALID_REQUIREMENT: 'INVALID_REQUIREMENT',
  SYSTEM_ERROR: 'SYSTEM_ERROR'
} as const;

export type PermissionErrorCode = typeof PermissionErrorCodes[keyof typeof PermissionErrorCodes];

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

/**
 * Permission system configuration
 */
export const PERMISSION_CONFIG = {
  CACHE_TTL_DEFAULT: 5 * 60 * 1000, // 5 minutes
  CACHE_TTL_USER_ROLE: 15 * 60 * 1000, // 15 minutes (roles change less frequently)
  CACHE_TTL_PROJECT_MEMBERSHIP: 2 * 60 * 1000, // 2 minutes (project permissions change more frequently)
  DEBUG_MODE: true, // Enable debug mode for development
  MAX_CACHE_SIZE: 1000,
  PERFORMANCE_THRESHOLD_MS: 10
} as const;

/**
 * Role hierarchy levels (higher number = more permissions)
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [_UserRole.ADMIN]: 3,
  [_UserRole.COMPANY_ADMIN]: 2,
  [_UserRole.USER]: 1
};

/**
 * Project permission hierarchy levels (higher number = more permissions)
 */
export const PROJECT_PERMISSION_HIERARCHY: Record<ProjectPermission, number> = {
  [_ProjectPermission.OWNER]: 4,
  [_ProjectPermission.EDITOR]: 3,
  [_ProjectPermission.OPERATOR]: 2,
  [_ProjectPermission.VIEWER]: 1
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Helper type for extracting permission keys
 */
export type PermissionKey = `${PermissionResource}.${PermissionAction}`;

/**
 * Permission matrix type for comprehensive permission management
 */
export type PermissionMatrix = {
  [_K in UserRole]: {
    [_R in PermissionResource]: {
      [_A in PermissionAction]: boolean;
    };
  };
};

/**
 * Type guard helpers
 */
export const isUserRole = (role: unknown): role is UserRole =>
  Object.values(_UserRole).includes(role as UserRole);

export const isProjectPermission = (permission: unknown): permission is ProjectPermission =>
  Object.values(_ProjectPermission).includes(permission as ProjectPermission);

export const isPermissionAction = (action: unknown): action is PermissionAction =>
  Object.values(_PermissionAction).includes(action as PermissionAction);

export const isPermissionResource = (resource: unknown): resource is PermissionResource =>
  Object.values(_PermissionResource).includes(resource as PermissionResource);